import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Upload, Sparkles, Loader2, Check, Download, Send, RotateCcw, AlertTriangle,
  Camera, Undo2, Trash2, Wand2, X, GripVertical, Plus,
} from "lucide-react";
import { toast } from "sonner";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import api, { API } from "@/lib/api";
import { STUDIO_STYLES, TILE_TYPES, COLOURS, BUDGETS, ROOM_TYPES, AI_DISCLAIMER, CONTACT } from "@/data/site";

const BASE = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id) => `${API}/studio/image/${id}`;

const resizeToBase64 = (file, max = 1280) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-10">
    {[1, 2, 3].map((n) => (
      <React.Fragment key={n}>
        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-mono ${step >= n ? "bg-rossi-gold text-rossi-bg" : "bg-rossi-surface text-rossi-stone border border-white/10"}`}>
          {step > n ? <Check size={16} /> : n}
        </div>
        {n < 3 && <div className={`flex-1 h-[2px] ${step > n ? "bg-rossi-gold" : "bg-white/10"}`} />}
      </React.Fragment>
    ))}
  </div>
);

// ---------------- Customizer ----------------
const Customizer = ({ design, original, baseId, setBaseId, overlays, setOverlays, fixtures, busy, applySwap, customText, setCustomText, onSelect }) => {
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const [activeCat, setActiveCat] = useState(fixtures[0]?.key || "");
  const [selUid, setSelUid] = useState(null);

  const activeItems = fixtures.find((c) => c.key === activeCat)?.items || [];

  const addOverlay = (item) => {
    const uid = Math.random().toString(36).slice(2);
    setOverlays((o) => [...o, { uid, url: `${BASE}${item.image}`, x: 0.38, y: 0.38, w: 0.24, name: item.name }]);
    setSelUid(uid);
    toast.success(`${item.name} placed — drag to position`);
  };
  const removeOverlay = (uid) => { setOverlays((o) => o.filter((x) => x.uid !== uid)); setSelUid(null); };

  const onPointerDown = (e, uid, mode) => {
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const ov = overlays.find((o) => o.uid === uid);
    drag.current = { uid, mode, rect, startX: e.clientX, startY: e.clientY, ox: ov.x, oy: ov.y, ow: ov.w };
    setSelUid(uid);
  };

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      const dx = (e.clientX - d.startX) / d.rect.width;
      const dy = (e.clientY - d.startY) / d.rect.height;
      setOverlays((list) => list.map((o) => {
        if (o.uid !== d.uid) return o;
        if (d.mode === "resize") return { ...o, w: Math.max(0.05, Math.min(0.9, d.ow + dx)) };
        return { ...o, x: Math.max(-0.1, Math.min(0.95, d.ox + dx)), y: Math.max(-0.1, Math.min(0.95, d.oy + dy)) };
      }));
    };
    const up = () => { drag.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [setOverlays]);

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="studio-customizer">
      {/* CANVAS */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-mono uppercase tracking-widest text-rossi-stone mr-auto">Your design — customise it</span>
          <div className="flex gap-2">
            <input
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Describe a change (e.g. add a large window)"
              className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50 w-64"
              data-testid="custom-instruction"
            />
            <button onClick={() => customText && applySwap(customText)} disabled={busy || !customText} className="btn-ghost-gold !py-2 disabled:opacity-40" data-testid="custom-apply"><Wand2 size={15} /> Apply</button>
          </div>
        </div>
        <div ref={canvasRef} className="relative rounded-xl overflow-hidden border border-rossi-gold/20 bg-rossi-surface select-none" style={{ touchAction: "none" }} data-testid="design-canvas" onPointerDown={() => setSelUid(null)}>
          <img src={imgUrl(baseId)} alt="Your design" className="w-full block pointer-events-none" draggable={false} />
          {overlays.map((o) => (
            <div key={o.uid} className={`absolute ${selUid === o.uid ? "ring-2 ring-rossi-gold" : ""}`} style={{ left: `${o.x * 100}%`, top: `${o.y * 100}%`, width: `${o.w * 100}%` }} onPointerDown={(e) => onPointerDown(e, o.uid, "move")} data-testid={`overlay-${o.uid}`}>
              <img src={o.url} alt={o.name} className="w-full block pointer-events-none drop-shadow-2xl" draggable={false} />
              {selUid === o.uid && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); removeOverlay(o.uid); }} className="absolute -top-3 -right-3 h-7 w-7 rounded-full bg-rossi-bg border border-rossi-gold/50 text-rossi-gold flex items-center justify-center" data-testid="overlay-remove"><X size={14} /></button>
                  <div onPointerDown={(e) => onPointerDown(e, o.uid, "resize")} className="absolute -bottom-2 -right-2 h-6 w-6 rounded-full bg-rossi-gold text-rossi-bg flex items-center justify-center cursor-nwse-resize" title="Drag to resize"><GripVertical size={13} /></div>
                </>
              )}
            </div>
          ))}
          {busy && (
            <div className="absolute inset-0 bg-rossi-bg/70 flex flex-col items-center justify-center" data-testid="canvas-busy">
              <Loader2 size={36} className="text-rossi-gold animate-spin" />
              <p className="text-sm text-rossi-cream mt-3">Reworking your design…</p>
            </div>
          )}
        </div>
        <p className="text-xs text-rossi-stone mt-2">Tip: “Place” drops a product you can drag &amp; resize. “Swap in” asks the AI to blend it into the photo realistically.</p>
      </div>

      {/* FIXTURE PANEL */}
      <div className="card-lux p-4 h-fit lg:sticky lg:top-24">
        <div className="flex flex-wrap gap-1.5 mb-4">
          {fixtures.map((c) => (
            <button key={c.key} onClick={() => setActiveCat(c.key)} className={`px-2.5 py-1.5 rounded-full text-xs ${activeCat === c.key ? "bg-rossi-gold text-rossi-bg" : "border border-white/10 text-rossi-cream/70 hover:border-rossi-gold/40"}`} data-testid={`fixture-cat-${c.key}`}>{c.label}</button>
          ))}
        </div>
        <div className="space-y-3 max-h-[460px] overflow-auto pr-1">
          {activeItems.map((it) => (
            <div key={it.id} className="flex gap-3 items-center border-b border-white/5 pb-3 last:border-0" data-testid={`fixture-item-${it.id}`}>
              <img src={it.image} alt={it.name} className={`h-14 w-14 ${it.surface ? "object-cover" : "object-contain"} bg-white/5 rounded-lg p-1`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-rossi-cream truncate">{it.name}</p>
                <p className="text-xs text-rossi-gold font-mono">£{it.price_gbp}{it.unit || ""}</p>
                <div className="flex gap-1.5 mt-1.5">
                  {!it.surface && (
                    <button onClick={() => { onSelect(it.id); addOverlay(it); }} className="text-[11px] px-2 py-1 rounded-full border border-white/15 text-rossi-cream/80 hover:border-rossi-gold/50 flex items-center gap-1" data-testid={`place-${it.id}`}><Plus size={11} /> Place</button>
                  )}
                  <button onClick={() => { onSelect(it.id); applySwap(it.swap_prompt); }} disabled={busy} className="text-[11px] px-2 py-1 rounded-full bg-rossi-gold/15 text-rossi-gold hover:bg-rossi-gold/25 flex items-center gap-1 disabled:opacity-40" data-testid={`swap-${it.id}`}><Wand2 size={11} /> {it.surface ? "Apply" : "Swap in"}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function PhotoStudio() {
  const [step, setStep] = useState(1);
  const [image, setImage] = useState(null);
  const [roomType, setRoomType] = useState("Bathroom");
  const [style, setStyle] = useState("Modern Italian");
  const [colors, setColors] = useState(["Cream"]);
  const [tileType, setTileType] = useState("Large format porcelain");
  const [budget, setBudget] = useState("Standard");
  const [dims, setDims] = useState({ length: "", width: "", height: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(0);

  // customizer state
  const [fixtures, setFixtures] = useState([]);
  const [baseId, setBaseId] = useState(null);
  const [history, setHistory] = useState([]);
  const [overlays, setOverlays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [customText, setCustomText] = useState("");
  const [selections, setSelections] = useState([]);

  const [lead, setLead] = useState({ name: "", email: "", phone: "", postcode: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/studio/fixtures").then((r) => setFixtures(r.data || [])).catch(() => {}); }, []);

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Please upload a JPEG, PNG or WEBP image."); return; }
    setImage(await resizeToBase64(file));
    toast.success("Room photo added");
  }, []);

  const toggleColour = (name) => setColors((c) => (c.includes(name) ? c.filter((x) => x !== name) : [...c, name]));

  const generate = async () => {
    if (!image) { toast.error("Please upload or scan a photo of your room first."); setStep(1); return; }
    if (!consent) { toast.error("Please accept the AI design consent to continue."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/design", {
        image_base64: image, room_type: roomType, style, colors, tile_type: tileType, budget,
        dimensions: { length: parseFloat(dims.length) || null, width: parseFloat(dims.width) || null, height: parseFloat(dims.height) || null },
      });
      setResult(data);
      setSelected(0);
      setBaseId(data.renders[0]);
      setHistory([]); setOverlays([]); setSelections([]);
      toast.success("Your designs are ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Design generation failed. Please try again.");
    } finally { setLoading(false); }
  };

  const selectRender = (i) => {
    setSelected(i);
    setBaseId(result.renders[i]);
    setHistory([]); setOverlays([]); setSelections([]);
  };

  const applySwap = async (instruction) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/customize", { base_image_id: baseId, instruction });
      setHistory((h) => [...h, baseId]);
      setBaseId(data.image_id);
      setCustomText("");
      toast.success("Design updated");
    } catch (e) { toast.error("Could not update the design. Please try again."); }
    finally { setBusy(false); }
  };

  const undo = () => {
    setHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setBaseId(prev);
      return h.slice(0, -1);
    });
  };
  const resetDesign = () => { setBaseId(result.renders[selected]); setHistory([]); setOverlays([]); };

  const finalize = async () => {
    if (!lead.name || !lead.email) { toast.error("Please add your name and email so our team can follow up."); return; }
    setSending(true);
    try {
      await api.post("/ai/finalize", {
        design_id: result.id,
        base_image_id: baseId,
        overlays: overlays.map((o) => ({ url: o.url, x: o.x, y: o.y, w: o.w })),
        name: lead.name, email: lead.email, phone: lead.phone, postcode: lead.postcode,
        room_type: roomType, style,
        selections,
        public_base: BASE,
      });
      setSent(true);
      toast.success("Your design has been sent to our design team");
    } catch (e) { toast.error("Could not send your design. Please try again or call us."); }
    finally { setSending(false); }
  };

  const reset = () => {
    setResult(null); setStep(1); setImage(null); setSent(false); setSelected(0);
    setBaseId(null); setHistory([]); setOverlays([]); setSelections([]);
  };

  return (
    <div data-testid="photo-studio">
      <section className="pb-24 max-w-6xl mx-auto">
        {!result && (
          <div className="card-lux p-6 sm:p-10 max-w-5xl mx-auto">
            <Stepper step={step} />

            {step === 1 && (
              <div data-testid="studio-step-1">
                <h2 className="font-serif text-2xl text-rossi-cream mb-6">1. Scan or upload your room</h2>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Which room?</label>
                <div className="flex flex-wrap gap-2 mb-8">
                  {ROOM_TYPES.map((r) => (
                    <button key={r} onClick={() => setRoomType(r)} data-testid={`room-${r.toLowerCase().replace(/\s+/g, "-")}`} className={`px-4 py-2 rounded-full text-sm ${roomType === r ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}>{r}</button>
                  ))}
                </div>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Take a photo of your space (required — it’s the room we’ll redesign)</label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="cursor-pointer card-lux border-dashed border-2 border-rossi-gold/25 hover:border-rossi-gold/50 transition-colors p-8 flex flex-col items-center justify-center text-center">
                    <Upload size={26} className="text-rossi-gold mb-2" /><span className="text-sm text-rossi-cream/80">Upload a photo</span><span className="text-xs text-rossi-stone mt-1">JPEG, PNG or WEBP</span>
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="studio-upload" />
                  </label>
                  <label className="cursor-pointer card-lux border-dashed border-2 border-rossi-gold/25 hover:border-rossi-gold/50 transition-colors p-8 flex flex-col items-center justify-center text-center">
                    <Camera size={26} className="text-rossi-gold mb-2" /><span className="text-sm text-rossi-cream/80">Scan with camera</span><span className="text-xs text-rossi-stone mt-1">On mobile devices</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="studio-camera" />
                  </label>
                </div>
                {image && <img src={image} alt="Your room" className="mt-5 rounded-xl h-56 w-full object-cover border border-rossi-gold/20" data-testid="uploaded-preview" />}
                <div className="flex justify-end mt-8"><button onClick={() => { if (!image) { toast.error("Please add a photo of your room."); return; } setStep(2); }} className="btn-gold" data-testid="studio-next-1">Continue</button></div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="studio-step-2">
                <h2 className="font-serif text-2xl text-rossi-cream mb-6">2. What style do you love?</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                  {STUDIO_STYLES.map((s) => (
                    <button key={s} onClick={() => setStyle(s)} data-testid={`style-${s.toLowerCase().replace(/\s+|&/g, "-")}`} className={`px-4 py-3 rounded-xl text-sm text-left ${style === s ? "bg-rossi-gold text-rossi-bg font-medium" : "card-lux text-rossi-cream/80 hover:border-rossi-gold/40"}`}>{s}</button>
                  ))}
                </div>
                <div className="flex justify-between mt-4"><button onClick={() => setStep(1)} className="btn-ghost-gold">Back</button><button onClick={() => setStep(3)} className="btn-gold" data-testid="studio-next-2">Continue</button></div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="studio-step-3">
                <h2 className="font-serif text-2xl text-rossi-cream mb-6">3. Colours, tiles &amp; budget</h2>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Colour palette</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {COLOURS.map((c) => (
                    <button key={c.name} onClick={() => toggleColour(c.name)} className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm border ${colors.includes(c.name) ? "border-rossi-gold bg-rossi-gold/10 text-rossi-cream" : "border-white/10 text-rossi-cream/70"}`}>
                      <span className="h-4 w-4 rounded-full border border-white/20" style={{ background: c.hex }} />{c.name}
                    </button>
                  ))}
                </div>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Preferred tile</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {TILE_TYPES.map((t) => (<button key={t} onClick={() => setTileType(t)} className={`px-3 py-2 rounded-full text-sm ${tileType === t ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}>{t}</button>))}
                </div>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Budget</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {BUDGETS.map((b) => (<button key={b} onClick={() => setBudget(b)} className={`px-4 py-2 rounded-full text-sm ${budget === b ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80 hover:bg-rossi-gold/10"}`}>{b}</button>))}
                </div>
                <label className="text-sm text-rossi-cream/80 mb-2 block">Room dimensions in metres (optional — powers material estimates)</label>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {["length", "width", "height"].map((k) => (<input key={k} type="number" step="0.1" placeholder={k[0].toUpperCase() + k.slice(1)} value={dims[k]} onChange={(e) => setDims({ ...dims, [k]: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid={`dim-${k}`} />))}
                </div>
                <label className="flex items-start gap-3 text-sm text-rossi-cream/75 mb-6 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-rossi-gold" data-testid="studio-consent" />
                  <span>I consent to my image and details being processed to generate AI design concepts. {AI_DISCLAIMER}</span>
                </label>
                <div className="flex justify-between"><button onClick={() => setStep(2)} className="btn-ghost-gold">Back</button><button onClick={generate} disabled={loading} className="btn-gold disabled:opacity-50" data-testid="studio-generate"><Sparkles size={16} /> Redesign My Room</button></div>
              </div>
            )}

            {loading && (
              <div className="py-16 text-center" data-testid="studio-loading">
                <Loader2 size={44} className="text-rossi-gold animate-spin mx-auto" />
                <p className="font-serif text-2xl text-rossi-cream mt-6">Redesigning your space…</p>
                <p className="text-sm text-rossi-stone mt-2">Rendering two photorealistic concepts from your photo. This can take up to a minute.</p>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-8" data-testid="studio-result">
            <div className="text-center"><p className="eyebrow justify-center mb-3">Your Studio concepts</p><h2 className="font-serif text-3xl sm:text-4xl text-rossi-cream">Pick a concept, then make it yours</h2></div>

            <div className="grid sm:grid-cols-2 gap-4" data-testid="render-grid">
              {result.renders?.map((rid, i) => (
                <button key={rid} onClick={() => selectRender(i)} data-testid={`render-${i}`} className={`text-left card-lux overflow-hidden transition-all ${selected === i ? "ring-2 ring-rossi-gold" : "hover:border-rossi-gold/40"}`}>
                  <img src={imgUrl(rid)} alt={`Concept ${i + 1}`} className="w-full h-56 object-cover" />
                  <div className="p-3 flex items-center justify-between"><span className="text-sm text-rossi-cream font-medium">Concept 0{i + 1}</span>{selected === i && <span className="text-xs text-rossi-gold">Selected</span>}</div>
                </button>
              ))}
            </div>

            {image && baseId && (
              <div className="card-lux p-3">
                <p className="text-xs font-mono uppercase tracking-widest text-rossi-stone px-2 pt-1 pb-2">Your room, transformed — drag to compare</p>
                <BeforeAfterSlider before={image} after={imgUrl(baseId)} beforeAlt="Your room now" afterAlt="Rossi concept" height="h-[380px]" />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={undo} disabled={!history.length || busy} className="btn-ghost-gold !py-2 disabled:opacity-40" data-testid="studio-undo"><Undo2 size={15} /> Undo</button>
              <button onClick={resetDesign} disabled={busy} className="btn-ghost-gold !py-2 disabled:opacity-40" data-testid="studio-reset-design"><RotateCcw size={15} /> Reset concept</button>
            </div>

            {baseId && fixtures.length > 0 && (
              <Customizer
                design={result} original={image} baseId={baseId} setBaseId={setBaseId}
                overlays={overlays} setOverlays={setOverlays} fixtures={fixtures}
                busy={busy} applySwap={applySwap} customText={customText} setCustomText={setCustomText}
                onSelect={(id) => setSelections((s) => (s.includes(id) ? s : [...s, id]))}
              />
            )}

            {result.materials && Object.keys(result.materials).length > 0 && (
              <div className="card-lux p-7">
                <h3 className="font-serif text-xl text-rossi-cream mb-4">Preliminary material estimate</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  {[["Floor area", "floor_area_m2", "m²"], ["Wall area", "wall_area_m2", "m²"], ["Total tiling", "total_tiling_area_m2", "m²"], ["Tiles to order", "tiles_to_order_m2", "m²"], ["Adhesive", "adhesive_kg", "kg"], ["Grout", "grout_kg", "kg"]].map(([label, key, unit]) =>
                    result.materials[key] != null ? (<div key={key} className="bg-rossi-bg rounded-lg p-3"><p className="text-xs text-rossi-stone">{label}</p><p className="font-mono text-rossi-gold text-lg">{result.materials[key]} {unit}</p></div>) : null)}
                </div>
              </div>
            )}

            <div className="flex gap-3 rounded-xl border border-rossi-gold/25 bg-rossi-gold/5 p-5 text-sm text-rossi-cream/80"><AlertTriangle size={18} className="text-rossi-gold shrink-0 mt-0.5" /><p>{AI_DISCLAIMER}</p></div>

            {!sent ? (
              <div className="card-lux p-7" data-testid="studio-send-form">
                <h3 className="font-serif text-2xl text-rossi-cream mb-2">Happy with your design?</h3>
                <p className="text-sm text-rossi-cream/70 mb-5">Send your finished design to our design team and we’ll be in touch to refine it and prepare a quote. Add your details so we can reach you.</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <input placeholder="Your name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="lead-name" />
                  <input placeholder="Email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="lead-email" />
                  <input placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="lead-phone" />
                  <input placeholder="Postcode" value={lead.postcode} onChange={(e) => setLead({ ...lead, postcode: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="lead-postcode" />
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={finalize} disabled={sending} className="btn-gold disabled:opacity-50" data-testid="studio-send">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send my design</button>
                  <a href={`${API}/designs/${result.id}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost-gold" data-testid="studio-download"><Download size={16} /> Download Project Pack</a>
                  <button onClick={reset} className="btn-ghost-gold" data-testid="studio-restart"><RotateCcw size={16} /> Start over</button>
                </div>
              </div>
            ) : (
              <div className="card-lux p-8 text-center" data-testid="studio-sent">
                <Check size={40} className="text-rossi-gold mx-auto" />
                <h3 className="font-serif text-2xl text-rossi-cream mt-4">Your design has been sent to our team</h3>
                <p className="text-sm text-rossi-cream/70 mt-2">Thank you, {lead.name.split(" ")[0]}. Our design team has your concept and will be in touch shortly.</p>
                <div className="flex flex-wrap gap-3 justify-center mt-6">
                  <a href={`${API}/designs/${result.id}/pdf`} target="_blank" rel="noreferrer" className="btn-gold"><Download size={16} /> Download Project Pack</a>
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/design/${result.id}`); toast.success("Shareable link copied — send it to family!"); }} className="btn-ghost-gold" data-testid="studio-share"><Send size={16} /> Copy shareable link</button>
                  <Link to="/booking" className="btn-ghost-gold">Book a consultation</Link>
                  <a href={CONTACT.phoneHref} className="btn-ghost-gold">Call {CONTACT.phone}</a>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
